// src/components/PresenceSheet.js
// D-063: Tappable "+N more" opens a bottom sheet (mobile) / centered modal (desktop)
// with the full viewer list.
//
// The default Workouts page surface (pattern #29) still renders up to 5 chips below
// the player with a "+N more" affordance. Clicking that affordance now opens this
// component, which lists every viewer in first-name + last-initial format.
//
// Render contract:
//   <PresenceSheet open viewers={liveViewers} onClose={...} />
//
// Close paths:
//   - Backdrop click
//   - Escape key
//   - Close (×) button in the sheet
//
// Responsive layout:
//   - Mobile (< 600px): full-width bottom sheet, rounded top corners, slides up
//   - Desktop (≥ 600px): centered modal card with 2-column chip grid

import React, { useEffect } from 'react';
import AvatarPhoto from './AvatarPhoto';
import { useMemberPhotos, getCachedMemberTagline } from '../services/memberProfiles';

export default function PresenceSheet({ open, viewers, onClose }) {
    // Escape key = close
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        // Lock background scroll while sheet is open
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    // Photos looked up by userId; presence objects are untouched (arrayUnion/
    // arrayRemove match on exact equality). Only fetch while the sheet is open.
    const photoFor = useMemberPhotos(open ? (viewers || []).map((v) => v.userId) : []);

    if (!open) return null;

    const total = viewers?.length || 0;

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 3000,
                background: 'rgba(23, 20, 24, 0.55)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                animation: 'fadeIn 0.2s ease-out',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label={`${total} viewers in class`}
                className="mom-presence-sheet"
                style={{
                    width: '100%',
                    maxWidth: 520,
                    maxHeight: '75vh',
                    background: 'var(--mom-white)',
                    borderTopLeftRadius: 20, borderTopRightRadius: 20,
                    boxShadow: '0 -20px 60px rgba(23, 20, 24, 0.25)',
                    padding: '20px 20px 24px',
                    display: 'flex', flexDirection: 'column',
                    animation: 'slideUp 0.25s ease-out',
                }}
            >
                {/* Drag handle (decorative on mobile) */}
                <div style={{
                    width: 40, height: 4, borderRadius: 2,
                    background: 'var(--mom-cream-dark)',
                    margin: '0 auto 16px',
                }} />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
                            letterSpacing: '0.1em', textTransform: 'uppercase',
                            color: 'var(--mom-warm-gray)', margin: 0, marginBottom: 2,
                        }}>In class</p>
                        <h3 style={{
                            fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontStyle: 'italic',
                            fontWeight: 600, color: 'var(--mom-charcoal)', margin: 0,
                        }}>
                            {total} {total === 1 ? 'viewer' : 'viewers'}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'var(--mom-cream)', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'background 0.15s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--mom-cream-dark)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--mom-cream)'; }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--mom-charcoal)" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Viewer list — flexWrap grid of chips */}
                <div style={{
                    flex: 1, overflowY: 'auto',
                    display: 'flex', flexWrap: 'wrap', gap: 8,
                    paddingRight: 4,
                }}>
                    {total === 0 ? (
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontStyle: 'italic',
                            color: 'var(--mom-warm-gray)', margin: '20px auto',
                        }}>No one in the room yet — hang tight.</p>
                    ) : (
                        viewers.map((v, i) => {
                            const first = (v.firstName || '').trim();
                            const last = (v.lastInitial || '').trim();
                            const displayName = first
                                ? (last ? `${first} ${last.charAt(0).toUpperCase()}` : first)
                                : '—';
                            const avatarLetter = (first || '?').charAt(0).toUpperCase();
                            return (
                                <div key={(v.userId || '') + '_' + i} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%', minWidth: 0,
                                    padding: '4px 14px 4px 4px', borderRadius: 999,
                                    background: 'var(--mom-cream)',
                                    border: '1px solid var(--mom-cream-dark)',
                                }}>
                                    <div style={{
                                        width: 26, height: 26, borderRadius: '50%',
                                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', flexShrink: 0,
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
                                        letterSpacing: '-0.02em', position: 'relative',
                                    }}>{avatarLetter}<AvatarPhoto url={photoFor(v.userId)} /></div>
                                    <span style={{
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                                        color: 'var(--mom-charcoal)', whiteSpace: 'nowrap',
                                    }}>{displayName}</span>
                                    {/* Tagline shown inline here (PHOTO-002) — the full list has room. */}
                                    {getCachedMemberTagline(v.userId) && (
                                        <span style={{
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 12.5,
                                            color: 'color-mix(in srgb, var(--mom-charcoal) 68%, var(--mom-cream))',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1,
                                        }}>· {getCachedMemberTagline(v.userId)}</span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
